import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import axios from 'axios'

/**
 * Quick Calculator Modal
 * Provides fast access to calculation tools without navigating away
 * Uses the user's preferred calculation config automatically
 */
const QuickCalculator = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [config, setConfig] = useState(null)
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)

  // Plant rows for multiple sizes at once
  const [plantRows, setPlantRows] = useState([
    { size: 'mediumPlants', quantity: 0, difficulty: 'standard', soilType: 'loamy' }
  ])

  const [gravelInputs, setGravelInputs] = useState({
    type: 'halfInch',
    squareFeet: 1000,
    depth: 2,
    installMethod: 'hand',
    difficulty: 'standard'
  })

  const [irrigationInputs, setIrrigationInputs] = useState({
    plantCount: 120,
    treeCount: 12,
    includeBackflow: true,
    includeClock: true
  })

  const [lightingInputs, setLightingInputs] = useState({
    lightCount: 20,
    wattsPerLight: 10
  })

  useEffect(() => {
    if (open) {
      loadPreferredConfig()
    }
  }, [open])

  const loadPreferredConfig = async () => {
    try {
      setLoading(true)
      // Load all configs
      const configsResponse = await axios.get('/api/calculation-configs')
      const allConfigs = configsResponse.data.configs || []
      setConfigs(allConfigs)

      // Load user's preferred config
      const prefResponse = await axios.get('/api/calculation-configs/user/preference')
      const preferredConfigId = prefResponse.data.configId

      if (preferredConfigId) {
        const preferredConfig = allConfigs.find(c => c._id === preferredConfigId)
        setConfig(preferredConfig || allConfigs[0])
      } else {
        setConfig(allConfigs[0])
      }
    } catch (err) {
      console.error('Error loading config:', err)
    } finally {
      setLoading(false)
    }
  }

  const getConfigValue = (path) => {
    if (!config) return 0
    const parts = path.split('.')
    let value = config
    for (const part of parts) {
      value = value?.[part]
    }
    return value || 0
  }

  // Plant calculation - sum all rows
  const calculatePlants = () => {
    let totalLaborDays = 0
    let totalLaborCost = 0
    const dayRate = getConfigValue('labor.dayRate')

    plantRows.forEach(row => {
      if (row.quantity > 0) {
        const rate = getConfigValue(`plantRates.${row.size}`)
        const difficultyFactor = getConfigValue(`difficultyFactors.${row.difficulty}`)
        const soilFactor = getConfigValue(`soilFactors.${row.soilType}`)

        const plantsPerDay = rate
        const adjustedRate = plantsPerDay / (difficultyFactor * soilFactor)
        const laborDays = row.quantity / adjustedRate
        const laborCost = laborDays * dayRate

        totalLaborDays += laborDays
        totalLaborCost += laborCost
      }
    })

    const totalManHours = totalLaborDays * 8 // 8 hours per day

    return {
      laborDays: totalLaborDays.toFixed(2),
      manHours: totalManHours.toFixed(1),
      dayRate: dayRate.toFixed(2),
      laborCost: totalLaborCost.toFixed(2),
      rows: plantRows.filter(r => r.quantity > 0).length
    }
  }

  const addPlantRow = () => {
    setPlantRows([...plantRows, { size: 'mediumPlants', quantity: 0, difficulty: 'standard', soilType: 'loamy' }])
  }

  const removePlantRow = (index) => {
    if (plantRows.length > 1) {
      setPlantRows(plantRows.filter((_, i) => i !== index))
    }
  }

  const updatePlantRow = (index, field, value) => {
    const newRows = [...plantRows]
    newRows[index][field] = value
    setPlantRows(newRows)
  }

  // Gravel calculation
  const calculateGravel = () => {
    const coverage = getConfigValue(`gravelCoverage.${gravelInputs.type}`)
    const installRate = gravelInputs.installMethod === 'hand'
      ? getConfigValue('materialRates.gravelTonsPerDay')
      : gravelInputs.installMethod === 'tractor'
        ? getConfigValue('moveGravelRates.gravelTonsPerDayTractor')
        : getConfigValue('moveGravelRates.gravelTonsPerDayBobcat')

    const dayRate = getConfigValue('labor.dayRate')
    const equipmentRate = gravelInputs.installMethod === 'tractor'
      ? getConfigValue('equipment.tractorRentalDailyRate')
      : gravelInputs.installMethod === 'bobcat'
        ? getConfigValue('equipment.bobcatRentalDailyRate')
        : 0

    const difficultyFactor = getConfigValue(`difficultyFactors.${gravelInputs.difficulty}`)
    const depthMultiplier = gravelInputs.depth / 2

    const tonsNeeded = (gravelInputs.squareFeet / coverage) * depthMultiplier
    const cubicYards = tonsNeeded / 1.5 // Average gravel density: 1.5 tons per cubic yard
    const adjustedRate = installRate / difficultyFactor
    const laborDays = tonsNeeded / adjustedRate

    const materialCost = tonsNeeded >= 10
      ? tonsNeeded * getConfigValue('gravel.costPerTonOver9')
      : tonsNeeded * getConfigValue('gravel.costPerTonUnder9')

    const laborCost = laborDays * dayRate
    const equipmentCost = laborDays * equipmentRate
    const totalCost = materialCost + laborCost + equipmentCost

    return {
      tonsNeeded: tonsNeeded.toFixed(2),
      cubicYards: cubicYards.toFixed(2),
      laborDays: laborDays.toFixed(2),
      materialCost: materialCost.toFixed(2),
      laborCost: laborCost.toFixed(2),
      equipmentCost: equipmentCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  // Irrigation calculation
  const calculateIrrigation = () => {
    const plantsPerValve = getConfigValue('irrigation.plantsPerValve')
    const treesPerValve = getConfigValue('irrigation.treesPerValve')
    const daysPerValve = getConfigValue('irrigation.daysPerValve')
    const costPerValve = getConfigValue('irrigation.costPerValve')
    const clockCost = getConfigValue('irrigation.clockCost')
    const backflowCost = getConfigValue('irrigation.backflowCost')
    const dayRate = getConfigValue('labor.dayRate')

    const plantValves = Math.ceil(irrigationInputs.plantCount / plantsPerValve)
    const treeValves = Math.ceil(irrigationInputs.treeCount / treesPerValve)
    const totalValves = plantValves + treeValves

    const materialCost = (totalValves * costPerValve) +
      (irrigationInputs.includeClock ? clockCost : 0) +
      (irrigationInputs.includeBackflow ? backflowCost : 0)

    const laborDays = totalValves * daysPerValve
    const laborCost = laborDays * dayRate
    const totalCost = materialCost + laborCost

    return {
      totalValves,
      laborDays: laborDays.toFixed(2),
      materialCost: materialCost.toFixed(2),
      laborCost: laborCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  // Lighting calculation
  const calculateLighting = () => {
    const costPerLight = getConfigValue('lighting.costPerLight')
    const techDailyRate = getConfigValue('lighting.techDailyRate')
    const lightsPerDay = getConfigValue('lighting.lightsInstalledPerDay')
    const hoursTrenchingPerLight = getConfigValue('lighting.hoursTrenchingPerLight')
    const wiringCostPerLight = getConfigValue('lighting.wiringAndConnectorCostPerLight')
    const hourlyTrenchingRate = getConfigValue('lighting.hourlyTrenchingRate')

    const totalWattage = lightingInputs.lightCount * lightingInputs.wattsPerLight
    let transformerCost = 0
    let transformerSize = ''

    if (totalWattage <= 150) {
      transformerCost = getConfigValue('lighting.transformerCosts.w150')
      transformerSize = '150W'
    } else if (totalWattage <= 300) {
      transformerCost = getConfigValue('lighting.transformerCosts.w300')
      transformerSize = '300W'
    } else if (totalWattage <= 600) {
      transformerCost = getConfigValue('lighting.transformerCosts.w600')
      transformerSize = '600W'
    } else if (totalWattage <= 900) {
      transformerCost = getConfigValue('lighting.transformerCosts.w900')
      transformerSize = '900W'
    } else if (totalWattage <= 1200) {
      transformerCost = getConfigValue('lighting.transformerCosts.w1200')
      transformerSize = '1200W'
    } else {
      transformerCost = getConfigValue('lighting.transformerCosts.custom')
      transformerSize = 'Custom'
    }

    const lightMaterialCost = lightingInputs.lightCount * costPerLight
    const wiringCost = lightingInputs.lightCount * wiringCostPerLight
    const installDays = lightingInputs.lightCount / lightsPerDay
    const trenchingHours = lightingInputs.lightCount * hoursTrenchingPerLight

    const materialCost = lightMaterialCost + wiringCost + transformerCost
    const installCost = installDays * techDailyRate
    const trenchingCost = trenchingHours * hourlyTrenchingRate
    const totalCost = materialCost + installCost + trenchingCost

    return {
      totalWattage,
      transformerSize,
      installDays: installDays.toFixed(2),
      materialCost: materialCost.toFixed(2),
      installCost: installCost.toFixed(2),
      trenchingCost: trenchingCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  const results = activeTab === 0 ? calculatePlants() :
                  activeTab === 1 ? calculateGravel() :
                  activeTab === 2 ? calculateIrrigation() :
                  calculateLighting()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Quick Calculator</Typography>
          {config && configs.length > 0 && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 250 }}>
              <Select
                value={config._id}
                onChange={(e) => {
                  const selectedConfig = configs.find(c => c._id === e.target.value)
                  setConfig(selectedConfig)
                }}
              >
                {configs.map(cfg => (
                  <MenuItem key={cfg._id} value={cfg._id}>
                    {cfg.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : !config ? (
          <Alert severity="warning">
            No calculation configuration found. Please set up a calculation config in Settings.
          </Alert>
        ) : (
          <Box>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              style={{ marginBottom: 24 }}
            >
              <Tab label="Plants" />
              <Tab label="Gravel" />
              <Tab label="Irrigation" />
              <Tab label="Lighting" />
            </Tabs>

            <Grid container spacing={3}>
              {/* Left: Inputs */}
              <Grid item xs={12} md={6}>
                {/* Plant Inputs */}
                {activeTab === 0 && (
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                        Plant Installation
                      </Typography>
                      <Button size="small" variant="outlined" color="primary" onClick={addPlantRow}>
                        + Add Row
                      </Button>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Size</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell>Difficulty</TableCell>
                            <TableCell>Soil</TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {plantRows.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Select
                                  value={row.size}
                                  onChange={(e) => updatePlantRow(index, 'size', e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                >
                                  <MenuItem value="flatFlowers">Flat</MenuItem>
                                  <MenuItem value="pintFlowers">Pint</MenuItem>
                                  <MenuItem value="quartFlowers">Quart</MenuItem>
                                  <MenuItem value="smallPlants">#1</MenuItem>
                                  <MenuItem value="mediumPlants">#5</MenuItem>
                                  <MenuItem value="largePlants">#15</MenuItem>
                                  <MenuItem value="mediumTrees">24"</MenuItem>
                                  <MenuItem value="largeTrees">36"+</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  value={row.quantity}
                                  onChange={(e) => updatePlantRow(index, 'quantity', parseInt(e.target.value) || 0)}
                                  variant="outlined"
                                  size="small"
                                  inputProps={{ min: 0, style: { textAlign: 'right', width: 60 } }}
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={row.difficulty}
                                  onChange={(e) => updatePlantRow(index, 'difficulty', e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                >
                                  <MenuItem value="veryEasy">Easy</MenuItem>
                                  <MenuItem value="standard">Std</MenuItem>
                                  <MenuItem value="difficult">Hard</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={row.soilType}
                                  onChange={(e) => updatePlantRow(index, 'soilType', e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                >
                                  <MenuItem value="sandy">Sandy</MenuItem>
                                  <MenuItem value="loamy">Loamy</MenuItem>
                                  <MenuItem value="clay">Clay</MenuItem>
                                  <MenuItem value="rocky">Rocky</MenuItem>
                                </Select>
                              </TableCell>
                              <TableCell>
                                {plantRows.length > 1 && (
                                  <Button
                                    size="small"
                                    color="secondary"
                                    onClick={() => removePlantRow(index)}
                                  >
                                    ×
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Gravel Inputs */}
                {activeTab === 1 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                      Gravel/Rip-Rap
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={gravelInputs.type}
                            onChange={(e) => setGravelInputs({...gravelInputs, type: e.target.value})}
                            label="Type"
                          >
                            <MenuItem value="quarterInch">1/4"</MenuItem>
                            <MenuItem value="halfInch">1/2"</MenuItem>
                            <MenuItem value="threeQuarterInch">3/4"</MenuItem>
                            <MenuItem value="oneInch">1"</MenuItem>
                            <MenuItem value="ripRap">Rip-Rap</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Square Feet"
                          value={gravelInputs.squareFeet}
                          onChange={(e) => setGravelInputs({...gravelInputs, squareFeet: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Depth (inches)"
                          value={gravelInputs.depth}
                          onChange={(e) => setGravelInputs({...gravelInputs, depth: parseFloat(e.target.value) || 0})}
                          inputProps={{ step: 0.5 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth variant="outlined" size="small">
                          <InputLabel>Install Method</InputLabel>
                          <Select
                            value={gravelInputs.installMethod}
                            onChange={(e) => setGravelInputs({...gravelInputs, installMethod: e.target.value})}
                            label="Install Method"
                          >
                            <MenuItem value="hand">By Hand</MenuItem>
                            <MenuItem value="tractor">With Tractor</MenuItem>
                            <MenuItem value="bobcat">With Bobcat</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Irrigation Inputs */}
                {activeTab === 2 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                      Irrigation System
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Plant Count"
                          value={irrigationInputs.plantCount}
                          onChange={(e) => setIrrigationInputs({...irrigationInputs, plantCount: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Tree Count"
                          value={irrigationInputs.treeCount}
                          onChange={(e) => setIrrigationInputs({...irrigationInputs, treeCount: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" gap={1}>
                          <Button
                            variant={irrigationInputs.includeBackflow ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => setIrrigationInputs({...irrigationInputs, includeBackflow: !irrigationInputs.includeBackflow})}
                          >
                            Backflow
                          </Button>
                          <Button
                            variant={irrigationInputs.includeClock ? "contained" : "outlined"}
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => setIrrigationInputs({...irrigationInputs, includeClock: !irrigationInputs.includeClock})}
                          >
                            Clock
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Lighting Inputs */}
                {activeTab === 3 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                      Landscape Lighting
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Light Count"
                          value={lightingInputs.lightCount}
                          onChange={(e) => setLightingInputs({...lightingInputs, lightCount: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          type="number"
                          label="Watts/Light"
                          value={lightingInputs.wattsPerLight}
                          onChange={(e) => setLightingInputs({...lightingInputs, wattsPerLight: parseInt(e.target.value) || 0})}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>

              {/* Right: Results */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                  Results
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {activeTab === 0 && (
                        <>
                          <TableRow>
                            <TableCell>Plant Rows</TableCell>
                            <TableCell align="right">{results.rows}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Total Labor Days</TableCell>
                            <TableCell align="right">{results.laborDays}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Total Man Hours</TableCell>
                            <TableCell align="right"><strong>{results.manHours} hrs</strong></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Day Rate</TableCell>
                            <TableCell align="right">${results.dayRate}</TableCell>
                          </TableRow>
                          <TableRow style={{ backgroundColor: '#e8f5e9' }}>
                            <TableCell><strong>Total Labor Cost</strong></TableCell>
                            <TableCell align="right"><strong>${results.laborCost}</strong></TableCell>
                          </TableRow>
                        </>
                      )}

                      {activeTab === 1 && (
                        <>
                          <TableRow>
                            <TableCell>Tons Needed</TableCell>
                            <TableCell align="right">{results.tonsNeeded}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Cubic Yards</TableCell>
                            <TableCell align="right"><strong>{results.cubicYards} yd³</strong></TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Labor Days</TableCell>
                            <TableCell align="right">{results.laborDays}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Material Cost</TableCell>
                            <TableCell align="right">${results.materialCost}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Labor Cost</TableCell>
                            <TableCell align="right">${results.laborCost}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Equipment Cost</TableCell>
                            <TableCell align="right">${results.equipmentCost}</TableCell>
                          </TableRow>
                          <TableRow style={{ backgroundColor: '#e8f5e9' }}>
                            <TableCell><strong>Total Cost</strong></TableCell>
                            <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                          </TableRow>
                        </>
                      )}

                      {activeTab === 2 && (
                        <>
                          <TableRow>
                            <TableCell>Total Valves</TableCell>
                            <TableCell align="right">{results.totalValves}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Labor Days</TableCell>
                            <TableCell align="right">{results.laborDays}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Material Cost</TableCell>
                            <TableCell align="right">${results.materialCost}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Labor Cost</TableCell>
                            <TableCell align="right">${results.laborCost}</TableCell>
                          </TableRow>
                          <TableRow style={{ backgroundColor: '#e8f5e9' }}>
                            <TableCell><strong>Total Cost</strong></TableCell>
                            <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                          </TableRow>
                        </>
                      )}

                      {activeTab === 3 && (
                        <>
                          <TableRow>
                            <TableCell>Total Wattage</TableCell>
                            <TableCell align="right">{results.totalWattage}W</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Transformer</TableCell>
                            <TableCell align="right">{results.transformerSize}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Install Days</TableCell>
                            <TableCell align="right">{results.installDays}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Material Cost</TableCell>
                            <TableCell align="right">${results.materialCost}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Install Cost</TableCell>
                            <TableCell align="right">${results.installCost}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Trenching Cost</TableCell>
                            <TableCell align="right">${results.trenchingCost}</TableCell>
                          </TableRow>
                          <TableRow style={{ backgroundColor: '#e8f5e9' }}>
                            <TableCell><strong>Total Cost</strong></TableCell>
                            <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default QuickCalculator
